import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { Menu, ShoppingCart, User as UserIcon, X } from "lucide-react";

const NAV = [
  { to: "/servicos", label: "Serviços" },
  { to: "/materiais", label: "Materiais" },
  { to: "/loja", label: "Loja" },
  { to: "/portfolio", label: "Portefólio" },
  { to: "/sobre", label: "Sobre" },
  { to: "/contactos", label: "Contactos" },
];

export default function PublicLayout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="border-b border-border sticky top-0 z-40 bg-background/95 backdrop-blur-sm">
        <div className="container-x flex items-center justify-between h-24">
          <Link to="/" className="flex items-center" data-testid="nav-home">
            <Logo size={72} />
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm link-underline ${isActive ? "text-brand" : "text-foreground/80 hover:text-foreground"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/pedido-orcamento" className="btn-primary text-xs" data-testid="nav-quote-cta">
              Pedir orçamento
            </Link>
            <Link to="/carrinho" className="relative p-2" data-testid="nav-cart">
              <ShoppingCart size={18} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand text-white text-[10px] mono rounded-full w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <div className="flex items-center gap-2">
                <Link
                  to={user.role === "customer" ? "/portal" : "/admin"}
                  className="p-2"
                  data-testid="nav-portal"
                >
                  <UserIcon size={18} />
                </Link>
                <button onClick={logout} className="text-xs text-muted-foreground hover:text-foreground" data-testid="nav-logout">
                  Sair
                </button>
              </div>
            ) : (
              <Link to="/login" className="text-sm link-underline" data-testid="nav-login">
                Entrar
              </Link>
            )}
          </div>
          <button className="md:hidden p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-border bg-background">
            <div className="container-x py-3 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="text-sm py-1" onClick={() => setOpen(false)}>
                  {n.label}
                </Link>
              ))}
              <Link to="/pedido-orcamento" className="btn-primary text-xs w-fit" onClick={() => setOpen(false)}>
                Pedir orçamento
              </Link>
              <Link to="/carrinho" className="text-sm" onClick={() => setOpen(false)}>
                Carrinho ({count})
              </Link>
              {user ? (
                <>
                  <Link to={user.role === "customer" ? "/portal" : "/admin"} className="text-sm" onClick={() => setOpen(false)}>
                    A minha conta
                  </Link>
                  <button onClick={logout} className="text-sm text-left">Sair</button>
                </>
              ) : (
                <Link to="/login" className="text-sm">Entrar</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border mt-24">
        <div className="container-x py-14 grid md:grid-cols-4 gap-10">
          <div>
            <Logo size={88} />
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
              Modelação 3D, prototipagem e fabrico aditivo. Da ideia ao produto físico.
            </p>
            <p className="tech-label mt-6">Barcelos · Portugal</p>
          </div>
          <div>
            <p className="tech-label mb-3">Serviços</p>
            <ul className="space-y-2 text-sm">
              <li><Link className="link-underline" to="/servicos#modelacao">Modelação 3D</Link></li>
              <li><Link className="link-underline" to="/servicos#impressao">Impressão 3D</Link></li>
              <li><Link className="link-underline" to="/servicos#prototipagem">Prototipagem</Link></li>
              <li><Link className="link-underline" to="/servicos#desenvolvimento">Desenvolvimento de produto</Link></li>
              <li><Link className="link-underline" to="/servicos#series">Pequenas séries</Link></li>
              <li><Link className="link-underline" to="/materiais">Materiais</Link></li>
              <li><Link className="link-underline text-brand" to="/ferramentas/analise-stl">Analisador STL</Link></li>
            </ul>
          </div>
          <div>
            <p className="tech-label mb-3">Empresa</p>
            <ul className="space-y-2 text-sm">
              <li><Link className="link-underline" to="/sobre">Sobre</Link></li>
              <li><Link className="link-underline" to="/portfolio">Portefólio</Link></li>
              <li><Link className="link-underline" to="/contactos">Contactos</Link></li>
              <li><Link className="link-underline" to="/pedido-orcamento">Pedir orçamento</Link></li>
            </ul>
          </div>
          <div>
            <p className="tech-label mb-3">Legal</p>
            <ul className="space-y-2 text-sm">
              <li><Link className="link-underline" to="/legal/termos">Termos e condições</Link></li>
              <li><Link className="link-underline" to="/legal/privacidade">Política de privacidade</Link></li>
              <li><Link className="link-underline" to="/legal/ficheiros">Política de ficheiros</Link></li>
              <li><Link className="link-underline" to="/legal/envios">Política de envios</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between gap-3 mono text-[11px] text-muted-foreground uppercase tracking-widest">
            <span>© {new Date().getFullYear()} cadditive · Barcelos, Portugal</span>
            <span>CAD · Additive Manufacturing</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
